// frontend/src/pages/admin/Dashboard.jsx
import { useEffect, useState, useCallback } from 'react';
import { getAgreements, deleteAgreement, resendAgreement } from '../../lib/api';
import AgreementForm from './AgreementForm';
import { C, inp, Btn, Card, Modal, Spinner, ErrBox, StatusPill, fmtDate } from '../../components/UI';

export default function Dashboard({ adminEmail, onLogout, showToast }) {
  const [view,        setView]        = useState('list');
  const [editTarget,  setEditTarget]  = useState(null);
  const [agreements,  setAgreements]  = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [apiErr,      setApiErr]      = useState('');
  const [search,      setSearch]      = useState('');
  const [filter,      setFilter]      = useState('all');
  const [previewItem, setPreviewItem] = useState(null);
  const [deleteItem,  setDeleteItem]  = useState(null);
  const [deleting,    setDeleting]    = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true); setApiErr('');
    try {
      const p = {};
      if (filter !== 'all') p.status = filter;
      if (search) p.search = search;
      setAgreements(await getAgreements(p));
    } catch (err) { setApiErr(err.message); }
    finally { setLoading(false); }
  }, [filter, search]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  async function handleDelete() {
    setDeleting(true);
    try { await deleteAgreement(deleteItem.id); showToast('Agreement deleted.'); setDeleteItem(null); fetchAll(); }
    catch (err) { showToast(err.message, 'error'); }
    finally { setDeleting(false); }
  }

  async function handleResend(agr) {
    try {
      const r = await resendAgreement(agr.id);
      if (r.emailError) showToast(r.emailError, 'error');
      else showToast(`Resent to ${agr.customer_email}`);
      fetchAll();
    } catch (err) { showToast(err.message, 'error'); }
  }

  if (view !== 'list') return (
    <AgreementForm existing={editTarget} onDone={() => { setView('list'); setEditTarget(null); fetchAll(); }} showToast={showToast} />
  );

  const stats = [
    { label: 'Total',   value: agreements.length,                              color: C.brand,   icon: '📄' },
    { label: 'Signed',  value: agreements.filter(a => a.status==='signed').length,  color: C.success, icon: '✅' },
    { label: 'Pending', value: agreements.filter(a => a.status==='pending').length, color: C.warning, icon: '⏳' },
    { label: 'Drafts',  value: agreements.filter(a => a.status==='draft').length,   color: C.muted,   icon: '📝' },
  ];

  return (
    <div>
      {deleteItem && (
        <Modal title="Delete Agreement" onClose={() => setDeleteItem(null)} width={420}>
          <p style={{ fontSize: 14, color: C.muted, marginBottom: 24, lineHeight: 1.6 }}>Delete <strong style={{ color: C.text }}>"{deleteItem.title}"</strong>? This cannot be undone.</p>
          <div style={{ display: 'flex', gap: 10 }}>
            <Btn variant="danger" disabled={deleting} onClick={handleDelete}>{deleting ? 'Deleting…' : 'Yes, Delete'}</Btn>
            <Btn variant="ghost" onClick={() => setDeleteItem(null)}>Cancel</Btn>
          </div>
        </Modal>
      )}

      {previewItem && (
        <Modal title="Agreement Preview" onClose={() => setPreviewItem(null)} width={660}>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>To: {previewItem.customer_email} · Sent: {fmtDate(previewItem.sent_at)}</div>
          <h3 style={{ fontSize: 17, fontWeight: 700, color: C.text, marginBottom: 16 }}>{previewItem.title}</h3>
          <div style={{ fontSize: 13.5, color: '#b0b0c8', lineHeight: 1.9, whiteSpace: 'pre-wrap', background: C.bg, borderRadius: 10, padding: '18px 20px', marginBottom: 20, maxHeight: 320, overflowY: 'auto', border: `1px solid ${C.border}` }}>{previewItem.content}</div>
          {previewItem.status === 'signed' && (
            <div style={{ background: 'rgba(34,197,94,.08)', border: '1px solid rgba(34,197,94,.2)', borderRadius: 10, padding: '13px 18px', marginBottom: 18, fontSize: 13 }}>
              ✅ <strong style={{ color: C.success }}>Signed</strong> by <em style={{ fontFamily: 'Georgia,serif', fontSize: 15, color: '#a0c4ff' }}>{previewItem.signature_name}</em> on {fmtDate(previewItem.signed_at)}
            </div>
          )}
          <div style={{ display: 'flex', gap: 10 }}>
            <Btn variant="primary" onClick={() => { setPreviewItem(null); setEditTarget(previewItem); setView('edit'); }}>✏️ Edit</Btn>
            <Btn variant="ghost" onClick={() => setPreviewItem(null)}>Close</Btn>
          </div>
        </Modal>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 30 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: C.text, letterSpacing: '-0.5px' }}>Dashboard</h1>
          <div style={{ fontSize: 13, color: C.muted, marginTop: 3 }}>Welcome back, {adminEmail}</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Btn variant="primary" onClick={() => { setEditTarget(null); setView('create'); }} style={{ boxShadow: `0 4px 16px ${C.brandGlow}` }}>+ New Agreement</Btn>
          <Btn variant="ghost" onClick={onLogout}>Sign Out</Btn>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 28 }}>
        {stats.map(s => (
          <div key={s.label} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: '20px 22px', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', right: 16, top: 16, fontSize: 24, opacity: 0.25 }}>{s.icon}</div>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.8 }}>{s.label}</div>
            <div style={{ fontSize: 34, fontWeight: 800, color: s.color }}>{s.value}</div>
            <div style={{ height: 3, borderRadius: 2, background: s.color, opacity: 0.2, marginTop: 14 }} />
          </div>
        ))}
      </div>

      <Card style={{ padding: 0 }}>
        <div style={{ padding: '18px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: C.muted, pointerEvents: 'none' }}>🔍</span>
            <input style={{ ...inp(), paddingLeft: 36 }} placeholder="Search name, email, title…" value={search} onChange={e => setSearch(e.target.value)}
              onFocus={e => e.target.style.borderColor = C.brand} onBlur={e => e.target.style.borderColor = C.border} />
          </div>
          <select style={{ ...inp(), width: 'auto', minWidth: 150, cursor: 'pointer' }} value={filter} onChange={e => setFilter(e.target.value)}>
            <option value="all">All Status</option>
            <option value="signed">Signed</option>
            <option value="pending">Pending</option>
            <option value="draft">Draft</option>
          </select>
        </div>

        <div style={{ padding: '0 24px 24px' }}>
          <ErrBox msg={apiErr} />
          {loading ? <Spinner /> : agreements.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: C.muted }}>
              <div style={{ fontSize: 44, marginBottom: 14 }}>📄</div>
              <div style={{ fontSize: 15, fontWeight: 500, color: C.text, marginBottom: 6 }}>No agreements yet</div>
              <div style={{ fontSize: 13 }}>Click "+ New Agreement" to get started</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 8 }}>
                <thead>
                  <tr>
                    {['Customer', 'Title', 'Created', 'Sent', 'Status', 'Actions'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '10px 12px', color: C.muted, fontWeight: 500, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6, borderBottom: `1px solid ${C.border}` }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {agreements.map(agr => (
                    <tr key={agr.id} style={{ borderBottom: `1px solid ${C.border}`, transition: 'background .1s' }}
                      onMouseEnter={e => e.currentTarget.style.background = C.surface2}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '14px 12px' }}>
                        <div style={{ fontWeight: 600, color: C.text }}>{agr.customer_name}</div>
                        <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{agr.customer_email}</div>
                      </td>
                      <td style={{ padding: '14px 12px', maxWidth: 200 }}>
                        <div style={{ color: '#c8c8e0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{agr.title}</div>
                      </td>
                      <td style={{ padding: '14px 12px', color: C.muted, whiteSpace: 'nowrap' }}>{fmtDate(agr.created_at)}</td>
                      <td style={{ padding: '14px 12px', color: C.muted, whiteSpace: 'nowrap' }}>{fmtDate(agr.sent_at)}</td>
                      <td style={{ padding: '14px 12px' }}><StatusPill status={agr.status} /></td>
                      <td style={{ padding: '14px 12px' }}>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <Btn size="sm" variant="ghost" onClick={() => setPreviewItem(agr)}>👁 View</Btn>
                          <Btn size="sm" variant="ghost" onClick={() => { setEditTarget(agr); setView('edit'); }}>✏️</Btn>
                          {agr.status !== 'signed' && <Btn size="sm" variant="success" onClick={() => handleResend(agr)}>📤 {agr.status === 'draft' ? 'Send' : 'Resend'}</Btn>}
                          <Btn size="sm" variant="danger" onClick={() => setDeleteItem(agr)}>🗑</Btn>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
